import React, { useEffect, useMemo, useState } from 'react';
import '@fontsource/lato';
import { Checkbox, Chip, Divider, IconButton, InputAdornment, Menu, MenuItem, Popover, TextField, } from '@mui/material';
import Button from '@mui/material/Button';
import { ArrowDropDownOutlined, FilterAltOutlined, PersonOutlined, SearchOutlined, } from '@mui/icons-material';
import { getUsersByPracticeId } from '~/api/settings/getUsersByPracticeId';
const roleOptions = [
    { value: '3', label: 'Provider' },
    { value: '4', label: 'Diabetes Educator/Dietitian' },
    { value: '5', label: 'Nurse' },
    { value: '6', label: 'Medical Assistant' },
    { value: '7', label: 'Office Manager' },
    { value: '8', label: 'Administrator' },
    { value: '9', label: 'Billing' },
    { value: '10', label: 'Other' },
    { value: '11', label: 'Clinical Account Manager' },
    { value: '12', label: 'Clinical Diabetes Specialist' },
    { value: '13', label: 'Patient Engagement & Account Manager' },
    { value: '14', label: 'Patient Engagement Specialist' },
];
const roleIdToLabel = (roleId) => {
    if (roleId == null)
        return '';
    const match = roleOptions.find(r => r.value === String(roleId));
    return match?.label ?? '';
};
const getField = (obj, ...keys) => {
    for (const k of keys) {
        if (obj && obj[k] != null && obj[k] !== '')
            return obj[k];
    }
    return undefined;
};
const getTeam = (user) => {
    const t = getField(user.personalInfo, 'team_name', 'teamName');
    return t || 'Other';
};
const getRoleId = (user) => getField(user.personalInfo, 'role_id', 'roleId');
const getOrg = (user) => user.personalInfo.accessLevel === 'SweetSpot' ? 'SweetSpot Staff' : 'Practice Staff';
const getFullName = (user) => `${user.personalInfo.firstname} ${user.personalInfo.lastname}`;
const QUICK_SELECT_TEAMS = ['Admin', 'Billing', 'Clinical', 'Device Troubleshooting', 'Enrollment', 'Other'];
const ORG_OPTIONS = ['SweetSpot Staff', 'Practice Staff'];
const TEAM_OPTIONS = ['Enrollment', 'Device Troubleshooting', 'Clinical', 'Admin', 'Billing', 'Other'];
export const SingleUserSelectorLayoutForPracticeId = (props) => {
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState(props.initialSelectedUser ?? null);
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const filterOpen = Boolean(filterAnchorEl);
    const [query, setQuery] = useState('');
    const [selectedFilters, setSelectedFilters] = useState(['Show Inactive Users']);
    const [selectedOrgFilters, setSelectedOrgFilters] = useState([...ORG_OPTIONS]);
    const [selectedTeamFilters, setSelectedTeamFilters] = useState([...TEAM_OPTIONS]);
    const [activeQuickSelect, setActiveQuickSelect] = useState(null);
    useEffect(() => {
        fetchUsers();
    }, []);
    const fetchUsers = async () => {
        const { users } = await getUsersByPracticeId({ practiceId: props.practiceId });
        const usersArray = users.map((user) => ({
            id: user.id,
            email: user.email,
            personalInfo: user.personalInfo,
            affiliations: [],
        }));
        usersArray.sort((a, b) => a.personalInfo.lastname.localeCompare(b.personalInfo.lastname));
        setUsers(usersArray);
    };
    const selectedUser = useMemo(() => users.find(u => u.id === selectedUserId) ?? null, [users, selectedUserId]);
    // Visible list: search + Show Inactive + org + team.
    const filteredUsers = useMemo(() => {
        const showInactive = selectedFilters.includes('Show Inactive Users');
        const q = query.trim().toLowerCase();
        return users.filter((user) => {
            const { firstname, lastname, inviteStatus } = user.personalInfo;
            if (!showInactive && inviteStatus === 'Inactive')
                return false;
            if (q && !`${firstname} ${lastname}`.toLowerCase().includes(q))
                return false;
            if (!selectedOrgFilters.includes(getOrg(user)))
                return false;
            if (!selectedTeamFilters.includes(getTeam(user)))
                return false;
            return true;
        });
    }, [users, query, selectedFilters, selectedOrgFilters, selectedTeamFilters]);
    // Pick a user — replaces selection and immediately commits + closes.
    const handleUserSelect = (user) => {
        setSelectedUserId(user.id);
        props.setSelectedUser(user.id);
        setAnchorEl(null);
    };
    const handleFilterSelect = (filter) => {
        setSelectedFilters(prev => prev.includes(filter) ? prev.filter(f => f !== filter) : [...prev, filter]);
    };
    // Org / Team toggles are pure visibility filters now — they don't touch selection.
    const handleOrgFilterToggle = (org) => {
        setSelectedOrgFilters(prev => prev.includes(org) ? prev.filter(f => f !== org) : [...prev, org]);
    };
    const handleTeamFilterToggle = (team) => {
        setSelectedTeamFilters(prev => {
            const next = prev.includes(team) ? prev.filter(f => f !== team) : [...prev, team];
            // Unchecking the team that drives the active chip clears the chip.
            if (activeQuickSelect && !next.includes(activeQuickSelect)) {
                setActiveQuickSelect(null);
            }
            return next;
        });
    };
    // Quick Select: narrows the team filter to just this team.
    // Clicking the active chip again clears it (restores all teams).
    const handleQuickSelect = (team) => {
        if (activeQuickSelect === team) {
            setActiveQuickSelect(null);
            setSelectedTeamFilters([...TEAM_OPTIONS]);
        }
        else {
            setActiveQuickSelect(team);
            setSelectedTeamFilters([team]);
        }
    };
    const buttonLabel = selectedUser
        ? getFullName(selectedUser)
        : (props.placeholder ?? 'Select a user');
    return (React.createElement(React.Fragment, null,
        React.createElement(Button, { style: {
                backgroundColor: 'white',
                height: '30px',
                borderRadius: '4px',
                borderColor: '#E4E4E7',
                borderWidth: '2px',
            }, endIcon: React.createElement(ArrowDropDownOutlined, null), onClick: (event) => setAnchorEl(event.currentTarget) }, buttonLabel),
        React.createElement(Popover, { anchorEl: anchorEl, open: open, onClose: () => setAnchorEl(null), anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: {
                style: {
                    width: 580,
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    fontFamily: 'Lato, sans-serif',
                },
            } },
            React.createElement("div", { style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                } },
                React.createElement("div", { style: { display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 700 } },
                    React.createElement(PersonOutlined, { style: { color: '#0000008A' } }),
                    React.createElement("span", { style: { color: '#0A0A0A', fontWeight: 500, fontSize: 18 } }, "Select a User"))),
            React.createElement(Divider, null),
            React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px' } },
                React.createElement(TextField, { fullWidth: true, size: "small", variant: "outlined", placeholder: "Search here", value: query, onChange: (e) => setQuery(e.target.value), InputProps: {
                        endAdornment: (React.createElement(InputAdornment, { position: "end" },
                            React.createElement(SearchOutlined, null))),
                    } }),
                React.createElement(IconButton, { onClick: (event) => setFilterAnchorEl(event.currentTarget), style: { border: '1px solid #E4E4E7', borderRadius: 4 } },
                    React.createElement(FilterAltOutlined, { style: { color: '#2C78EB' } })),
                React.createElement(Menu, { anchorEl: filterAnchorEl, open: filterOpen, onClose: () => setFilterAnchorEl(null) }, ['Show Inactive Users'].map(filter => (React.createElement(MenuItem, { key: filter, onClick: () => handleFilterSelect(filter) },
                    React.createElement(Checkbox, { checked: selectedFilters.includes(filter) }),
                    filter))))),
            React.createElement("div", { style: { padding: '0 16px 12px' } },
                React.createElement("div", { style: { fontSize: 14, color: '#717182', marginBottom: 8, fontFamily: 'Barlow' } }, "Quick Select:"),
                React.createElement("div", { style: { display: 'flex', flexWrap: 'wrap', gap: 8 } }, QUICK_SELECT_TEAMS.map(team => {
                    const active = activeQuickSelect === team;
                    return (React.createElement(Chip, { key: team, label: team, onClick: () => handleQuickSelect(team), style: {
                            backgroundColor: active ? '#2C78EB' : '#ECEEF2',
                            color: active ? '#FFFFFF' : '#030213',
                            cursor: 'pointer',
                            fontFamily: 'Barlow'
                        } }));
                }))),
            React.createElement("div", { style: { display: 'flex', gap: 16, padding: '0 16px 12px' } },
                React.createElement("div", { style: { flex: 1 } },
                    React.createElement("div", { style: { fontWeight: 700, marginBottom: 4, fontFamily: 'Barlow', color: '#0A0A0A' } }, "Organization"),
                    ORG_OPTIONS.map(org => (React.createElement("div", { key: org, style: { display: 'flex', alignItems: 'center', cursor: 'pointer' }, onClick: () => handleOrgFilterToggle(org) },
                        React.createElement(Checkbox, { checked: selectedOrgFilters.includes(org), size: "small" }),
                        React.createElement("span", { style: { color: '#0A0A0A', fontFamily: 'Barlow' } }, org))))),
                React.createElement("div", { style: { flex: 1 } },
                    React.createElement("div", { style: { fontWeight: 700, marginBottom: 4 } }, "Team"),
                    TEAM_OPTIONS.map(team => (React.createElement("div", { key: team, style: { display: 'flex', alignItems: 'center', cursor: 'pointer' }, onClick: () => handleTeamFilterToggle(team) },
                        React.createElement(Checkbox, { checked: selectedTeamFilters.includes(team), size: "small" }),
                        React.createElement("span", { style: { color: '#0A0A0A', fontFamily: 'Barlow' } }, team)))))),
            React.createElement(Divider, null),
            React.createElement("div", { style: { padding: '12px 16px', color: '#52525B' } },
                filteredUsers.length,
                " ",
                filteredUsers.length === 1 ? 'user' : 'users'),
            React.createElement("div", { style: { overflowY: 'auto', flex: 1, padding: '0 8px 12px' } },
                filteredUsers.map(user => {
                    const fullName = getFullName(user);
                    const roleLabel = roleIdToLabel(getRoleId(user));
                    const team = getTeam(user);
                    const orgLabel = user.personalInfo.accessLevel === 'SweetSpot' ? 'Sweetspot' : 'Practice';
                    const metaParts = [roleLabel, team, orgLabel].filter(Boolean);
                    const isSelected = selectedUserId === user.id;
                    return (React.createElement("div", { key: user.id, style: {
                            display: 'flex',
                            alignItems: 'flex-start',
                            padding: '6px 8px',
                            cursor: 'pointer',
                            borderRadius: 4,
                            backgroundColor: isSelected ? '#EAF2FE' : 'transparent',
                        }, onClick: () => handleUserSelect(user) },
                        React.createElement(Checkbox, { checked: isSelected, size: "small", style: { padding: 4, marginRight: 8 } }),
                        React.createElement("div", null,
                            React.createElement("div", { style: { fontWeight: 600, color: '#18181B', fontFamily: 'Barlow' } }, fullName),
                            React.createElement("div", { style: { fontSize: 13, color: '#717182', fontFamily: 'Barlow' } }, metaParts.join(' • ')))));
                }),
                filteredUsers.length === 0 && (React.createElement("div", { style: { padding: 16, color: '#717182', textAlign: 'center', fontFamily: 'Barlow' } }, "No users match these filters."))))));
};
